import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { spawnCharacter } from '../entities/npc.js';
import { CHARACTERS } from '../entities/characters.js';
import { PirateRaid } from '../minigames/pirateRaid.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, addCrumbs, spendCrumbs, setFlag, getFlag } from '../core/save.js';
import { questsForGiver, acceptQuest, turnIn, isReady, QUESTS, flagObjective } from '../core/quests.js';
import '../ui/space.css';

// The Crovian System — Gary's home beyond Deep Space, on the poor farm world
// BZ-943. The Dog Pirates are Robin Hood figures: they raid Elite Trucking and
// give the spoils to worlds the monopoly has squeezed. Gary is on their side.
// The through-line prop is the quadra-valve.

const VOID = 0x080312;
const CROVIAN = 0x8E5AC8;
const ELITE_BLUE = 0x3D5FC0;
const LOCO_BURGER_COST = 25;
const QUADRA_VALVE_COST = 100;

export function createCrovianScene(){ return () => new CrovianScene(); }

class CrovianScene {
  constructor(){
    this.panels = [];
    this.floatT = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(VOID);
    this.scene.fog = new THREE.Fog(VOID, 28, 78);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 140);
    this.camDist = 18; this.camTarget = new THREE.Vector3(0, 1.6, 1);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildAsteroids();
    this._buildStars();
    this._buildCrovianPlanet();
    this._buildMothership();
    this._buildTruck();
    this._buildTradePost();
    this._buildEliteSign();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 8);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.npcs = this._makeNPCs();
    this.npcs.forEach(n => this.scene.add(n.root));

    this.hotspots = [
      { pos: this.slice.root.position.clone(), r: 2.4, label: '🗣 talk to Slice', action: () => this._talkToSlice() },
      { pos: this.tito.root.position.clone(), r: 2.4, label: '🛒 Tito\'s Farm Emporium', action: () => this._talkToTito() },
      { pos: this.al.root.position.clone(), r: 2.4, label: '🗣 talk to Al the Space Trucker', action: () => this._talkToAl() },
      { pos: new THREE.Vector3(5.5, 0, -2.5), r: 2.6, label: '🏴‍☠️ play Pirate Raid', action: () => this._playRaid() },
      { pos: new THREE.Vector3(0, 0, 11), r: 2.6, label: '← fly back to Deep Space', action: () => this.manager.goTo('deepSpace', { from: 'crovian' }) }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x706090, 0.55));
    const crovianSun = new THREE.DirectionalLight(0xD8C8FF, 0.6);
    crovianSun.position.set(-10, 14, 8); crovianSun.castShadow = true;
    crovianSun.shadow.mapSize.set(2048, 2048);
    const s = 26;
    crovianSun.shadow.camera.left = -s; crovianSun.shadow.camera.right = s;
    crovianSun.shadow.camera.top = s; crovianSun.shadow.camera.bottom = -s;
    this.scene.add(crovianSun);
    const tradeGlow = new THREE.PointLight(0xF7C948, 0.35, 14, 2);
    tradeGlow.position.set(-5, 2, 3); this.scene.add(tradeGlow);
  }

  _buildAsteroids(){
    const rock = new THREE.Mesh(new THREE.SphereGeometry(8, 28, 18), new THREE.MeshLambertMaterial({ color: 0x4A3A5A }));
    rock.position.y = -6.8; rock.scale.set(1, 0.42, 1); rock.receiveShadow = true; this.scene.add(rock);
    const spots = [[-9, -5], [9, -4], [-5, 9], [7, 7], [-10, 4]];
    spots.forEach(([x, z]) => {
      const s = 0.8 + Math.random() * 0.7;
      const a = new THREE.Mesh(new THREE.SphereGeometry(s, 12, 10), new THREE.MeshLambertMaterial({ color: 0x3A2E4A }));
      a.position.set(x, -0.2, z); a.scale.y = 0.65; a.castShadow = true; this.scene.add(a);
    });
    const sign = textSprite('BZ-943 · Crovian System', 24, '#EAE0FF', 5.0, 1.0);
    sign.position.set(0, 4.4, -8); this.scene.add(sign);
  }

  _buildStars(){
    const geo = new THREE.BufferGeometry(); const pts = [];
    for (let i = 0; i < 500; i++){
      const a = Math.random() * Math.PI * 2, el = Math.random() * Math.PI * 0.55, r = 70;
      pts.push(Math.cos(a) * Math.cos(el) * r, Math.sin(el) * r + 8, Math.sin(a) * Math.cos(el) * r);
    }
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    this.scene.add(new THREE.Points(geo, new THREE.PointsMaterial({ color: 0xFFFFFF, size: 0.35 })));
  }

  _buildCrovianPlanet(){
    const planet = sphere(7, CROVIAN, 22, 18, -42, this.scene, 28);
    planet.material = new THREE.MeshBasicMaterial({ color: CROVIAN });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(10, 0.6, 8, 32), new THREE.MeshBasicMaterial({ color: 0xB88FC4, transparent: true, opacity: 0.4 }));
    ring.position.set(22, 18, -42); ring.rotation.x = 0.4; this.scene.add(ring);
  }

  _buildMothership(){
    // The Dog Pirate mothership: a refrigerator cobbled together from other refrigerators,
    // with boosters and laser turrets strapped on.
    const g = new THREE.Group(); g.position.set(-18, 16, -28); this.scene.add(g);
    const fridge = (x, y, z, w, h, d, col) => box(w, h, d, col, x, y + h/2, z, g);
    fridge(0, 0, 0, 3.2, 5.0, 2.6, 0xC0C8D0);
    fridge(-1.2, 1.8, 0.4, 1.4, 2.4, 1.6, 0xA8B0C0);
    fridge(1.4, 0.8, -0.3, 1.0, 1.8, 1.2, 0xB0B8C8);
    // boosters
    const booster = (x, z) => { cyl(.35, .45, 1.2, 0xE8461E, x, .6, z, g, 12); cyl(.25, .3, .2, 0xFFE9A8, x, .1, z, g, 8).material = new THREE.MeshBasicMaterial({ color: 0xFFE9A8 }); };
    booster(-1.6, 1.2); booster(1.6, 1.2); booster(0, -1.4);
    // laser turrets
    cyl(.15, .15, .6, 0x5A5A5A, -1.6, 3.8, 1.4, g, 8).rotation.x = Math.PI/2;
    cyl(.15, .15, .6, 0x5A5A5A, 1.6, 3.8, 1.4, g, 8).rotation.x = Math.PI/2;
    const sign = textSprite('🐕 Pirate Mothership', 22, '#EAE0FF', 4.4, 1.0);
    sign.position.set(-18, 20, -28); this.scene.add(sign);
  }

  _buildTruck(){
    const g = new THREE.Group(); g.position.set(6, 0, 3); this.scene.add(g);
    box(2.2, 1.6, 3.6, 0x9AA0A6, 0, 1.0, 0, g);
    box(2.0, 1.3, 0.2, 0x78A0C8, 0, 1.1, 1.8, g);
    box(2.6, 0.25, 4.0, 0x6A6A72, 0, 0.12, 0, g);
    sphere(.16, 0xFFE9A8, -.7, .9, 1.85, g, 8).material = new THREE.MeshBasicMaterial({ color: 0xFFE9A8 });
    sphere(.16, 0xFFE9A8, .7, .9, 1.85, g, 8).material = new THREE.MeshBasicMaterial({ color: 0xFFE9A8 });
    // Elite Trucking logo stripe
    box(2.3, .15, 3.7, ELITE_BLUE, 0, 1.35, 0, g);
    const sign = textSprite('🚛 Al\'s Rig · Elite Trucking', 22, '#EAE0FF', 4.4, 0.9);
    sign.position.set(6, 2.8, 3); this.scene.add(sign);
  }

  _buildTradePost(){
    const g = new THREE.Group(); g.position.set(-5.5, 0, 3); this.scene.add(g);
    box(2.4, 2.0, 2.2, 0x8E5AC8, 0, 1.1, 0, g);
    cyl(1.6, 1.6, 0.12, 0xB88FC4, 0, 2.2, 0, g, 20);
    box(1.6, 0.25, 0.15, 0xF7C948, 0, 1.5, 1.15, g);
    // force shield over premium seed
    const shield = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.9, 0.9), new THREE.MeshBasicMaterial({ color: 0x88CCFF, transparent: true, opacity: 0.25 }));
    shield.position.set(0, 1.6, 1.4); g.add(shield);
    const sign = textSprite('🛒 Tito\'s Farm Emporium', 22, '#EAE0FF', 4.0, 0.85);
    sign.position.set(-5.5, 3.2, 3); this.scene.add(sign);
  }

  _buildEliteSign(){
    // holographic Elite Trucking billboard — the monopoly antagonist
    const g = new THREE.Group(); g.position.set(8, 0, -6); this.scene.add(g);
    box(3.2, 2.2, 0.2, 0x222222, 0, 1.2, 0, g);
    const glow = new THREE.Mesh(new THREE.PlaneGeometry(2.8, 1.8), new THREE.MeshBasicMaterial({ color: ELITE_BLUE, transparent: true, opacity: 0.6, side: THREE.DoubleSide }));
    glow.position.set(0, 1.2, 0.12); g.add(glow);
    const sign = textSprite(' Elite Trucking — We Own the Lanes ', 20, '#EAE0FF', 5.0, 0.85);
    sign.position.set(8, 2.8, -6); this.scene.add(sign);
  }

  _makeNPCs(){
    const list = [];

    this.slice = spawnCharacter(CHARACTERS.slice, {
      x: 2.5, z: -2, facing: Math.PI * 0.75, wander: 1.6, speed: 0.65, bubbleY: 2.0, giver: 'slice'
    });
    list.push(this.slice);

    this.yango = spawnCharacter(CHARACTERS.yango, {
      x: 4.5, z: -4, facing: -Math.PI * 0.4, wander: 1.2, speed: 0.5, bubbleY: 2.0
    });
    list.push(this.yango);

    this.chawar = spawnCharacter(CHARACTERS.chawar, {
      x: 0.5, z: -5, facing: Math.PI * 0.25, wander: 1.3, speed: 0.8, bubbleY: 1.7
    });
    list.push(this.chawar);

    this.tito = spawnCharacter(CHARACTERS.tito, {
      x: -5.5, z: 5, facing: Math.PI, wander: 0.8, speed: 0.4, bubbleY: 2.0
    });
    list.push(this.tito);

    this.jessica = spawnCharacter(CHARACTERS.jessica, {
      x: 4.8, z: 5.2, facing: -Math.PI * 0.6, wander: 0.5, speed: 0.2, bubbleY: 1.8
    });
    list.push(this.jessica);

    const al = CHARACTERS.al;
    if (al){
      this.al = spawnCharacter(al, { x: 6, z: 5.5, facing: -Math.PI * 0.6, wander: 1.2, speed: 0.45, bubbleY: 2.1 });
      list.push(this.al);
    }

    return list;
  }

  // ---- interactions ----
  _talkToSlice(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.slice.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z); this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderSliceDialog();
  }

  _renderSliceDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('slice');
    const rows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isReady(id)){
        rows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.objective.noun}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc} · 🍞 ${q.reward?.crumbs||0}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const extra = `<p class="q-dnone">"Welcome to BZ-943, intern! We raid Elite Trucking and give the crumbs back. Gary's one of us now."</p>`;
    const body = rows.length ? rows.join('') + extra : extra;

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Slice</h2>
        <div class="cafe-sub">captain of the Dog Pirates</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); HUD.refreshQuests(); this._renderSliceDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onSliceTurnIn(b.dataset.id));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _onSliceTurnIn(id){
    if (id === 'loco-burger'){
      if (!getFlag('fedChawar')){ HUD.say('Chawar still needs that Loco Burger. Talk to Tito.'); return; }
    }
    if (id === 'quadra-valve'){
      if (!getFlag('boughtQuadraValve')){ HUD.say('You need to buy the quadra-valve from Tito first.'); return; }
      if (!getFlag('deliveredQuadraValve')){ HUD.say('Something went wrong — the valve is still in your pack.'); return; }
    }
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs(); HUD.refreshQuests();
    this.bread.drawFace('yum');
    this._renderSliceDialog();
  }

  _talkToTito(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.tito.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z); this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderTitoDialog();
  }

  _renderTitoDialog(){
    this._clearPanels();
    const boughtValve = getFlag('boughtQuadraValve');
    const fedChawar = getFlag('fedChawar');

    let rows = '';
    if (!fedChawar && isActiveOrAvailable('loco-burger')){
      const afford = getCrumbs() >= LOCO_BURGER_COST;
      rows += `<div class="q-drow"><div><b>🍔 Loco Burger</b><span>Chawar's lunch · 🍞 ${LOCO_BURGER_COST}</span></div>
        <button class="cafe-btn buy-burger ${afford ? '' : 'disabled'}">buy</button></div>`;
    }
    if (!boughtValve && isActiveOrAvailable('quadra-valve')){
      const afford = getCrumbs() >= QUADRA_VALVE_COST;
      rows += `<div class="q-drow"><div><b>⚙️ Quadra-Valve</b><span>One left. Premium part · 🍞 ${QUADRA_VALVE_COST}</span></div>
        <button class="cafe-btn buy-valve ${afford ? '' : 'disabled'}">buy</button></div>`;
    }
    if (!rows) rows = '<p class="q-dnone">"No window shopping. Come back when you need real farm gear."</p>';

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Tito's Farm Emporium</h2>
        <div class="cafe-sub">BZ-943 farm equipment</div>
        <div class="q-drows">${rows}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    const bb = p.querySelector('.buy-burger'); if (bb) bb.onclick = () => this._buyBurger();
    const bv = p.querySelector('.buy-valve'); if (bv) bv.onclick = () => this._buyValve();
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _buyBurger(){
    if (getFlag('fedChawar')) return;
    if (getCrumbs() < LOCO_BURGER_COST){ HUD.say(`You need ${LOCO_BURGER_COST} crumbs for a Loco Burger.`); return; }
    spendCrumbs(LOCO_BURGER_COST); HUD.refreshCrumbs();
    setFlag('fedChawar', true);
    flagObjective('loco-burger', 'fedChawar');
    HUD.say('You bought a Loco Burger. Chawar is going to cry happy tears.');
    this._renderTitoDialog();
  }

  _buyValve(){
    if (getFlag('boughtQuadraValve')) return;
    if (getCrumbs() < QUADRA_VALVE_COST){ HUD.say(`You need ${QUADRA_VALVE_COST} crumbs for the quadra-valve.`); return; }
    spendCrumbs(QUADRA_VALVE_COST); HUD.refreshCrumbs();
    setFlag('boughtQuadraValve', true);
    setFlag('deliveredQuadraValve', true); // delivered to Slice immediately for flow
    flagObjective('quadra-valve', 'deliveredQuadraValve');
    HUD.say('Tito hands over the quadra-valve. Gary can fix the Pirates\' hyperdrive now!');
    this._renderTitoDialog();
  }

  _talkToAl(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.al.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z); this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Al the Space Trucker</h2>
        <div class="cafe-sub">Elite Trucking</div>
        <p class="q-dnone">"Eat plasma, space rocks! ...What? Nobody out-hauls Al! Jessica, stop nagging — it\'s diet soda!"</p>
        <p class="q-dnone">Jessica: "Your sodium level is concerning, Al."</p>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _playRaid(){
    this.bread.stop();
    const game = new PirateRaid({
      target: 10, duration: 25,
      onWin: () => {
        addCrumbs(20); HUD.refreshCrumbs();
        HUD.say('You tagged Elite Trucking drones and salvaged 20 crumbs for BZ-943!');
        this.bread.drawFace('yum');
      },
      onClose: () => {}
    });
    game.mount();
  }

  _panel(html){ const el = document.createElement('div'); el.className = 'cafe-panel'; el.innerHTML = html; document.body.appendChild(el); this.panels.push(el); return el; }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- lifecycle ----
  enter({ manager, from }){
    this.manager = manager; this.input = manager.input;
    this.input.useCamera(this.camera); this.input.polarRange = [0.25, 0.7];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('BZ-943 · Crovian System'); HUD.refreshCrumbs(); HUD.refreshQuests();
    HUD.say(from === 'deepSpace'
      ? 'You made it to BZ-943! The Dog Pirates raid Elite Trucking to help poor worlds like this one. Gary fits right in.'
      : 'BZ-943 — a dusty farm world in the Crovian System.');
    HUD.setHint('click to walk · talk to Slice · shop at Tito\'s · play Pirate Raid · fly back');
    HUD.hideHintSoon(9000);
    setFlag('visitedCrovian', true);
    flagObjective('crovian-welcome', 'visitedCrovian');
  }
  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    if (checkNpcClick(ray, this.npcs, this.bread)) return;
    const dir = ray.ray.direction; if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y; if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(Math.max(-14, Math.min(14, p.x)), 0, Math.max(-12, Math.min(12, p.z)));
    HUD.hideHint(); this.bread.walkTo(gp);
  }

  update(dt){
    this.floatT += dt;
    this.bread.update(dt);
    this.bread.root.position.y = Math.abs(Math.sin(this.floatT * 1.8)) * 0.1;
    this.npcs.forEach((n, i) => { n.update(dt, this.bread.root.position); n.root.position.y = Math.abs(Math.sin(this.floatT * 1.4 + i)) * 0.08; });

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){ this.camera.aspect = window.innerWidth / window.innerHeight; this.camera.updateProjectionMatrix(); }
}

function isActiveOrAvailable(id){
  const { available, active } = questsForGiver('slice');
  return available.includes(id) || active.includes(id);
}
